import { Minus, Plus } from 'lucide-react';
import { cx } from '../../utils/cx';
import { useFieldProps } from '../FormField/FormField';
import { IconButton } from '../IconButton/IconButton';
import s from './NumberStepper.module.css';

export type NumberStepperProps = {
  value: number;
  onValueChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  id?: string;
  /** Used in the +/− button labels, e.g. "quantity". */
  unitLabel?: string;
  className?: string;
  'aria-describedby'?: string;
};

/** Big, thumb-friendly −/+ with a typeable number in between. */
export function NumberStepper({
  value,
  onValueChange,
  min = 1,
  max = 99,
  step = 1,
  unitLabel = 'value',
  className,
  ...rest
}: NumberStepperProps) {
  const props = useFieldProps(rest);
  // Outside a FormField there's no <label>, so name the input from `unitLabel`.
  const selfLabel = 'id' in props && props.id ? undefined : unitLabel;
  const clamp = (n: number) => Math.min(max, Math.max(min, n));
  return (
    <div className={cx(s.stepper, className)}>
      <IconButton
        icon={Minus}
        label={`Decrease ${unitLabel}`}
        variant="secondary"
        disabled={value <= min}
        onClick={() => onValueChange(clamp(value - step))}
      />
      <input
        className={s.value}
        type="number"
        inputMode="numeric"
        min={min}
        max={max}
        step={step}
        value={value}
        aria-label={selfLabel}
        onChange={(e) => {
          const n = e.currentTarget.valueAsNumber;
          if (!Number.isNaN(n)) onValueChange(clamp(n));
        }}
        {...props}
      />
      <IconButton
        icon={Plus}
        label={`Increase ${unitLabel}`}
        variant="secondary"
        disabled={value >= max}
        onClick={() => onValueChange(clamp(value + step))}
      />
    </div>
  );
}
